import React, { useState, useEffect, useRef } from "react";
import { Table, Input, Button, Dropdown, Menu, message, Popover, List } from "antd";
import { Link } from "react-router-dom";
import { FilterOutlined, DownOutlined } from '@ant-design/icons';
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import exportService from "../../utils/exportService";
import PrintReportPreview from "../PrintReportPreview";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { renderDate } from "../../utils/dateFormatter";
import InvoicePreview from "../../components/popups/InvoicePreview";
import generateInvoice from "../../invoices/frontOffice";
import CryptoJS from "crypto-js";
import SelectCorporate from "../../components/ui/SelectCorporate";

const CheckOutGuestBillingSummary = () => {
  const businessDate = localStorage.getItem("business_date") || moment().format("YYYY-MM-DD");
  const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
  const suffix = `_business_date_${businessDate}_${timestamp}`;
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [outputType, setOutputType] = useState("pdf");
  const [corporateCompany, setCorporateCompany] = useState(null);
  const [hasSelectedRooms, setHasSelectedRooms] = useState(false);
  const currencyCode = localStorage.getItem("currencycode") || "$";
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const { Search } = Input;
  const secretKey = "abde100199199111";

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error("Failed to enter fullscreen:", err);
        });
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen:", err);
        });
        setFullScreen(false);
      }
    };

    const handleFullscreenChange = () => {
      setFullScreen(!!document.fullscreenElement);
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener("click", handleClick);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener("click", handleClick);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen during cleanup:", err);
        });
      }
    };
  }, []);

  useEffect(() => {
    fetchInvoices(dateRange.startDate, dateRange.endDate);
  }, []);

  const [dateRange, setDateRange] = useState({
    startDate: moment(businessDate).startOf("day"),
    endDate: moment(businessDate).startOf("day"),
  });

  const fetchInvoices = async (startDate, endDate) => {
    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      setListLoading(false);
      setError("Invalid date range provided.");
      setInvoices([]);
      setApplicableTaxes([]);
      return;
    }

    setListLoading(true);
    setError(null);
    const token = localStorage.getItem("token");

    if (!token) {
      setListLoading(false);
      setError("Authentication token missing. Please log in again.");
      setInvoices([]);
      setApplicableTaxes([]);
      window.location.href = "/login";
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/transactions/list-invoices`, {
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data || !Array.isArray(response.data.invoices)) {
        throw new Error("Invalid response format: invoices must be an array.");
      }

      const parsedInvoices = response.data.invoices.map((invoice) => {
        const amountPaid = typeof invoice.amount_paid === "string" && !isNaN(invoice.amount_paid)
          ? Number(invoice.amount_paid)
          : 0;

        return {
          ...invoice,
          total_amount: Number(invoice.total_amount) || 0,
          tax: Number(invoice.tax) || 0,
          amount_paid: amountPaid,
          grand_total: Number(invoice.grand_total) || 0,
          balance_due: Number(invoice.balance_due) || 0,
        };
      });

      setInvoices(parsedInvoices);
      setApplicableTaxes(response.data.applicableTaxes || []);
      return { data: { invoices: parsedInvoices, applicableTaxes: response.data.applicableTaxes || [] } };
    } catch (err) {
      console.error("Fetch invoices error details:", {
        message: err.message,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data,
        } : "No response",
        request: err.request ? err.request : "No request",
      });
      if (err.response && err.response.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        let errorMessage = "Failed to load invoices for the selected date range.";
        if (err.response && err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        toast.error(errorMessage, { autoClose: 3000 });
      }
      setInvoices([]);
      setApplicableTaxes([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleSwitchView = async (newOutputType) => {
    try {
      setPreviewLoading(true);
      const result = await generateInvoice(currentInvoiceId, "invoice", "preview", newOutputType);
      
      if (newOutputType === "html") {
        setInvoiceHtml(result);
        if (invoiceUrl) {
          URL.revokeObjectURL(invoiceUrl);
        }
        setInvoiceUrl(null);
      } else {
        if (!(result instanceof Blob)) {
          throw new Error("Generated invoice is not a valid Blob.");
        }
        const url = URL.createObjectURL(result);
        setInvoiceUrl(url);
        setInvoiceHtml(null);
      }

      setOutputType(newOutputType);
    } catch (error) {
      console.error("Error switching invoice view:", error);
      toast.error("Failed to switch view.", { autoClose: 3000 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDateChange = async (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        setError("Invalid date selected.");
        setInvoices([]);
        setApplicableTaxes([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        setError("Start date cannot be after end date.");
        setInvoices([]);
        setApplicableTaxes([]);
        return prevRange;
      }

      fetchInvoices(newRange.startDate, newRange.endDate)
        .then((response) => {
          setInvoices(response.data.invoices);
          setApplicableTaxes(response.data.applicableTaxes || []);
        })
        .catch((error) => {
          console.error("Error fetching invoices:", error);
          let errorMessage = "Failed to load invoices for the selected date range.";
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          setError(errorMessage);
          setInvoices([]);
          setApplicableTaxes([]);
          toast.error(errorMessage, { autoClose: 3000 });
        });

      return newRange;
    });
  };

  // Local filtering based on corporateCompany
  useEffect(() => {
    if (corporateCompany) {
      const filtered = invoices.filter(invoice => invoice.billed_to_company_id === corporateCompany);
      setInvoices(filtered);
    } else {
      // Reset to original invoices when no company is selected
      fetchInvoices(dateRange.startDate, dateRange.endDate); // Re-fetch to restore original data
    }
  }, [corporateCompany]);

  const filteredInvoices = invoices.filter((invoice) =>
    invoice.invoice_number.toLowerCase().includes(searchText.toLowerCase())
  );

  const taxColumns = applicableTaxes.map((taxName) => ({
    title: `${taxName} ${currencyCode}`,
    dataIndex: taxName,
    render: (amount) => `${(amount || 0).toFixed(2)}`,
    sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
  }));

  const baseColumns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
      render: (text, record) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleGenerateInvoice(record.id, "html");
          }}
          style={{ color: "#1890ff", textDecoration: "underline", cursor: "pointer" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Guest Name",
      render: (record) => `${record.guest_name}`,
      sorter: (a, b) => a.guest_name.localeCompare(b.guest_name),
    },
    {
      title: "Reservation ID",
      render: (record) => {
        try {
          const encryptedId = CryptoJS.AES.encrypt(
            record.reservation_id.toString(),
            secretKey
          ).toString();
          return (
            <Link
              to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
              className="colored-box reservation-no-box"
            >
              {record.booking_number}
            </Link>
          );
        } catch (error) {
          console.error("Encryption Error:", error);
          return <span className="colored-box reservation-no-box">Error</span>;
        }
      },
      sorter: (a, b) => a.reservation_id.toString().localeCompare(b.reservation_id.toString()),
    },
    {
      title: "Room No",
      render: (record) => `${record.room_number}`,
      sorter: (a, b) => a.room_number.localeCompare(b.room_number),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      render: (date) => (
        <span className="date-box check-in-box">{renderDate(date)}</span>
      ),
      sorter: (a, b) => moment(a.invoice_date).unix() - moment(b.invoice_date).unix(),
    },
    {
      title: `Total Amount ${currencyCode}`,
      dataIndex: "total_amount",
      sorter: (a, b) => a.total_amount - b.total_amount,
      render: (amount) => `${(amount || 0).toFixed(2)}`,
    },
    {
      title: `Tax ${currencyCode}`,
      dataIndex: "tax",
      render: (amount) => `${(amount || 0).toFixed(2)}`,
    },
    {
      title: `Grand Total ${currencyCode}`,
      dataIndex: "grand_total",
      sorter: (a, b) => a.grand_total - b.grand_total,
      render: (amount) => `${(amount || 0).toFixed(2)}`,
    },
    {
      title: "Billed To",
      dataIndex: "billed_to",
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      render: (name) => name || "-",
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: 'payment_method',
      render: (paymentMethods) => (
        <Popover
          content={
            <List
              dataSource={paymentMethods}
              renderItem={(item) => (
                <List.Item>
                  <div>
                    <strong>Date:</strong> {item.chargeDate} <br />
                    <strong>Mode:</strong> {item.paymentMode} <br />
                    <strong>Amount:</strong> {currencyCode}{item.totalAmount}
                  </div>
                </List.Item>
              )}
            />
          }
          title="Payment Methods"
          trigger="click"
        >
          <Button type="link">View Payments ({paymentMethods.length})</Button>
        </Popover>
      ),
    },
  ];

  const taxColumnIndex = baseColumns.findIndex((col) => col.dataIndex === "tax");
  const columns = [
    ...baseColumns.slice(0, taxColumnIndex),
    ...taxColumns,
    ...baseColumns.slice(taxColumnIndex),
  ];

  const exportHeaders = [
    { key: "invoice_number", title: "Invoice Number" },
    { key: "guest_name", title: "Guest Name / Reservation ID" },
    { key: "room_number", title: "Room No" },
    { key: "invoice_date", title: "Invoice Date" },
    { key: "total_amount", title: `Total Amount ${currencyCode}` },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      title: `${taxName} ${currencyCode}`,
    })),
    { key: "tax", title: `Tax ${currencyCode}` },
    { key: "grand_total", title: `Grand Total ${currencyCode}` },
  ];

  const exportData = (data) =>
    data.map((invoice) => ({
      invoice_number: invoice.invoice_number,
      guest_name: invoice.guest_name,
      room_number: invoice.room_number,
      invoice_date: renderDate(invoice.invoice_date),
      total_amount: (invoice.total_amount || 0).toFixed(2),
      ...applicableTaxes.reduce((acc, taxName) => ({
        ...acc,
        [taxName]: (invoice[taxName] || 0).toFixed(2),
      }), {}),
      tax: (invoice.tax || 0).toFixed(2),
      grand_total: (invoice.grand_total || 0).toFixed(2),
    }));

  const getTotals = (data) => {
    const totalInvoices = data.length;
    const totalGrandTotal = data.reduce((sum, inv) => sum + (inv.grand_total || 0), 0).toFixed(2);
    const taxTotals = applicableTaxes.reduce((acc, taxName) => ({
      ...acc,
      [taxName]: data.reduce((sum, inv) => sum + (inv[taxName] || 0), 0).toFixed(2),
    }), {});
    return [
      { key: "Total Invoices", value: totalInvoices },
      { key: "Total Grand Total", value: `${currencyCode}${totalGrandTotal}` },
      ...applicableTaxes.map((taxName) => ({
        key: `${taxName} Total`,
        value: `${currencyCode}${taxTotals[taxName]}`,
      })),
    ];
  };

  const handleExportXLS = () => {
    const totals = getTotals(filteredInvoices);
    exportService.exportToXLS(
      exportData(filteredInvoices),
      exportHeaders,
      `guest_billing_summary${suffix}.xlsx`,
      "Check Out / Guest Billing Summary",
      totals
    );
  };

  const handleExportCSV = () => {
    const totals = getTotals(filteredInvoices);
    exportService.exportToCSV(
      exportData(filteredInvoices),
      exportHeaders,
      `guest_billing_summary${suffix}.csv`,
      totals
    );
  };

  const handleExportPDF = async () => {
    const totals = getTotals(filteredInvoices);
    exportService.exportToPDF(
      exportData(filteredInvoices),
      exportHeaders,
      "Check Out / Guest Billing Summary",
      `guest_billing_summary${suffix}.pdf`,
      false,
      true,
      totals,
      totals
    );
  };

  const handlePrint = async () => {
    try {
      setPreviewLoading(true);
      const totals = getTotals(filteredInvoices);
      const pdfBlob = await exportService.exportToPDF(
        exportData(filteredInvoices),
        exportHeaders,
        "Check Out / Guest Billing Summary",
        null,
        true,
        true,
        totals,
        totals
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPdfVisible(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      message.error("Failed to load PDF preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={handleExportXLS}>Download XLS</Menu.Item>
      <Menu.Item key="csv" onClick={handleExportCSV}>Download CSV</Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>Print Preview</Menu.Item>
    </Menu>
  );

  const handleGenerateInvoice = async (invoiceId, outputType = "html") => {
    try {
      setPreviewLoading(true);
      setInvoiceModalVisible(true);

      const result = await generateInvoice(invoiceId, "invoice", "preview", outputType);
      
      if (outputType === "html") {
        setInvoiceHtml(result);
        if (invoiceUrl) {
          URL.revokeObjectURL(invoiceUrl);
        }
        setInvoiceUrl(null);
      } else {
        if (!(result instanceof Blob)) {
          throw new Error("Generated invoice is not a valid Blob.");
        }
        const url = URL.createObjectURL(result);
        setInvoiceUrl(url);
        setInvoiceHtml(null);
      }

      setCurrentInvoiceId(invoiceId);
      setOutputType(outputType);
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error("Failed to generate invoice.", { autoClose: 3000 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseInvoiceModal = () => {
    setInvoiceModalVisible(false);
    if (invoiceUrl) {
      URL.revokeObjectURL(invoiceUrl);
      setInvoiceUrl(null);
    }
    setInvoiceHtml(null);
    setCurrentInvoiceId(null);
  };

  const filterContent = (
    <div style={{ padding: 16, width: 250 }}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
          <i className="fas fa-building" style={{ marginRight: 8 }}></i> Corporate Company
        </label>
        <SelectCorporate
          onSelectCompany={(selected) => {
            setCorporateCompany(selected ? selected.value : null);
          }}
          selectedValue={corporateCompany}
          isDisabled={hasSelectedRooms}
        />
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">Check Out / Guest Billing Summary</h3>
            </div>
            <div className="col-md-8">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ padding: '0 10px' }}>
                <div className="d-flex align-items-center gap-2">
                  <DatePicker
                    initialSettings={{
                      singleDatePicker: false,
                      showDropdowns: true,
                      startDate: dateRange.startDate.toDate(),
                      endDate: dateRange.endDate.toDate(),
                      ranges: {
                        "Today": [dateRange.startDate.toDate(), dateRange.startDate.toDate()],
                        "This Week": [
                          moment(dateRange.startDate).startOf("week").toDate(),
                          moment(dateRange.startDate).endOf("week").toDate(),
                        ],
                        "This Month": [
                          moment(dateRange.startDate).startOf("month").toDate(),
                          moment(dateRange.startDate).endOf("month").toDate(),
                        ],
                        "Last Month": [
                          moment().subtract(1, "month").startOf("month").toDate(),
                          moment().subtract(1, "month").endOf("month").toDate(),
                        ],
                        "Next Month": [
                          moment(dateRange.startDate).add(1, "month").startOf("month").toDate(),
                          moment(dateRange.startDate).add(1, "month").endOf("month").toDate(),
                        ],
                      },
                      locale: {
                        format: "DD MMM YYYY",
                        applyLabel: "Select",
                        cancelLabel: "Cancel",
                      },
                    }}
                    onCallback={(start, end) => handleDateChange({ start, end })}
                  >
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                        style={{
                          width: "220px",
                          height: "38px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          textAlign: "center",
                          padding: "0 10px",
                          borderRight: "none",
                          backgroundColor: "#ffffff",
                          borderRadius: "4px 0 0 4px",
                        }}
                        readOnly
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text"
                          style={{
                            height: "38px",
                            backgroundColor: "#fff",
                            borderLeft: "none",
                            borderRadius: "0 4px 4px 0",
                            cursor: "pointer",
                            padding: "0 10px",
                          }}
                        >
                          <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                        </span>
                      </div>
                    </div>
                  </DatePicker>
                </div>
                <div style={{ flexGrow: 1, maxWidth: "400px", margin: "0 10px" }}>
                  <Search
                    placeholder="Search by Invoice Number"
                    allowClear
                    enterButton="Search"
                    size="large"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center flex-shrink-0 gap-2">
                  <div className="view-icons d-flex align-items-center gap-2">
                    <Popover content={filterContent} title="Filters" trigger="click" placement="bottom">
                      <a href="#" className="btn btn-link" onClick={(e) => e.preventDefault()}>
                        <i className="las la-filter" style={{ fontSize: "18px" }} />
                      </a>
                    </Popover>
                    <a href="#" className="btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                      <i className="las la-expand-arrows-alt" style={{ fontSize: "18px" }} />
                    </a>
                    <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                      <Button className="custom-btn-drop d-inline-flex align-items-center" type="text">
                        <i className="la la-download custom-icon me-2" style={{ fontSize: "18px" }} /> Download <DownOutlined className="ms-2" />
                      </Button>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PrintReportPreview
          visible={pdfVisible}
          onClose={() => {
            setPdfVisible(false);
            setPdfData(null);
            if (pdfData) URL.revokeObjectURL(pdfData);
          }}
          reportTitle="Check Out / Guest Billing Summary"
          propertyName="Your Property Name"
          data={exportData(filteredInvoices)}
          headers={exportHeaders}
          footerData={getTotals(filteredInvoices)}
          totalColumns={["total_amount", ...applicableTaxes, "tax", "grand_total"]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />

        {listLoading ? (
          <div id="loader-wrapper-">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            {error ? (
              <p className="text-danger text-center">{error}</p>
            ) : filteredInvoices.length === 0 ? (
              <div className="no-data-container text-center mt-4">
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>No Invoices</h3>
                <p>There are no invoices in the selected date range.</p>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredInvoices}
                pagination={{ pageSize: 30 }}
                className="table-striped"
                rowKey={(record) => record.id}
              />
            )}
          </div>
        )}

        <InvoicePreview
          showModal={invoiceModalVisible}
          onClose={handleCloseInvoiceModal}
          pdfUrl={invoiceUrl}
          htmlContent={invoiceHtml}
          guestDetails={{}}
          type="invoice"
          outputType={outputType}
          handleSwitchView={handleSwitchView}
          loading={previewLoading}
        />
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover draggable />
    </div>
  );
};

export default CheckOutGuestBillingSummary;